/**
 * Where the asset is, and how sure the page may be about it.
 *
 * Every surface that draws the property on a map (the header's address link,
 * the Location panel, the comps map's subject pin, "Open map") and every
 * lookup keyed on a coordinate (site intelligence, Street View, photos) used to
 * take the first coordinate any source offered, whatever address it was for.
 * That is how a pin for the Washington Monument ended up under a deal named
 * Washington Square: Google Places had chosen a street from the name, stored a
 * "rooftop" point for it, and nothing downstream asked whether a document had
 * ever stated that street.
 *
 * This is the one place that decides. The answer is a STATUS first and a
 * coordinate second:
 *
 *   asset       a street address a person or a document stands behind. Lookups
 *               may run for it; lat/lng are set once a street-level point
 *               exists and null while one is still being found.
 *   city        only a place name (or a point the geocoder could place only at
 *               city level) — including a package's city and state whose
 *               street was set aside as a name lookup's. The map may centre on
 *               it; nothing may be drawn as the building.
 *   unverified  something is on the deal but nothing supports it: a street a
 *               name lookup wrote with no stated city or state left to show, or
 *               an address the browser could not check at all. No coordinate,
 *               no lookups.
 *   none        nothing to place: no address at all, or a state alone
 *               (`isStateOnly` tells the two apart for the page's wording).
 *
 * Pure: it reads what it is given and never defaults a coordinate to 0,0.
 */
import type {
  DossierAddress,
  DossierPropertyMaster,
  PropertySiteIntel,
} from '@/services/dossierContext'
import { CA_PROVINCES, US_STATES } from '@/utils/geo'
import {
  masterGeocode,
  masterPointApplies,
  placesMatch,
  streetsMatch,
  type ResolvedAddress,
} from './addressPrecedence'

export type AssetPointStatus = 'asset' | 'city' | 'unverified' | 'none'
export type AssetPointSource = 'master' | 'browser' | 'server' | 'intel'

export type AssetPoint = {
  status: AssetPointStatus
  lat: number | null
  lng: number | null
  /** Which source supplied the coordinate; null when there is none. */
  source: AssetPointSource | null
  /** One line, for a tooltip or a note — why the status is what it is. */
  reason: string
}

type Point = { lat?: number | null; lng?: number | null }

export type AssetPointInput = {
  /** The server's account of the deal's address. Every key optional. */
  addressBlock?: Partial<DossierAddress> | null
  /** The address the browser settled by the same precedence. `full` is read
   *  only for a deal whose location text is its whole address. */
  resolved: (Pick<ResolvedAddress, 'street' | 'city' | 'state' | 'partSources' | 'withheld'> & { full?: string; zip?: string | null }) | null
  master?: DossierPropertyMaster | null
  /** The browser's geocode of the address text. */
  browser?: Point | null
  /** The server's media lookup of the address text. */
  server?: Point | null
  /** The server's site-intel lookup, with the precision it geocoded at. */
  intel?: (Point & { geocodePrecision?: PropertySiteIntel['geocodePrecision'] }) | null
}

/** A finite coordinate that is not the (0, 0) an un-geocoded record turns into. */
function usable(point: Point | null | undefined): { lat: number; lng: number } | null {
  const lat = point?.lat
  const lng = point?.lng
  if (typeof lat !== 'number' || typeof lng !== 'number') return null
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null
  if (lat === 0 && lng === 0) return null
  return { lat, lng }
}

function unverifiedReason(input: AssetPointInput): string | null {
  const block = input.addressBlock
  // Rows written before name lookups were retired. The server withholds them
  // now (and says so through `withheld`, which `resolved` already carries); a
  // server that predates that must still not be taken for a stated address.
  if (block?.partSources?.street === 'places') {
    return 'The street was chosen by a name lookup, not stated by a document.'
  }
  // A withheld street takes only itself away. A city or state a package stated
  // survives it and is a place the page can still show; "unverified" is for a
  // deal on which nothing else is left to show.
  const { resolved } = input
  if (
    resolved
    && !resolved.street
    && !resolved.city
    && !resolved.state
    && resolved.withheld.includes('street')
  ) {
    return 'The only street on record was chosen by a name lookup, not stated by a document.'
  }
  return null
}

export const STATE_ONLY_REASON = 'Only a state is on record, so there is no place to map.'
/** What every surface says about a settled address that is a state and nothing else. */
export const STATE_ONLY_NOTE = 'State only - street and city not stated in package.'
/** What an Address row reads while the dossier context that settles the address loads. */
export const ADDRESS_PENDING_TEXT = 'Checking address…'

/**
 * Did the settled address keep a state and nothing else? On a legacy deal that
 * is what is left when a name lookup's street and city are set aside, so the
 * page must say what is missing rather than print a bare "DC".
 */
export function isStateOnly(
  resolved: Pick<ResolvedAddress, 'street' | 'city' | 'state'> | null | undefined,
): boolean {
  return Boolean(resolved && resolved.state && !resolved.street && !resolved.city)
}

// Every state, territory and province the catalogue's State field holds (utils/geo.ts, the table the server's
// vocabulary is held to) — the list here used to be typed again and left the territories out.
const US_AND_CA_STATES = new Set([...Object.keys(US_STATES), ...Object.keys(CA_PROVINCES)])
const QUADRANTS = new Set(['N', 'S', 'E', 'W', 'NE', 'NW', 'SE', 'SW'])
/** The whole-string "City, ST" / "City ST 12345" the server's parser accepts
 *  (`location._CLEAN_CITY_ST_RE`) over a settled address, which
 *  `property_media.parse_settled_address` reads after raising each word's first
 *  letter: a city of up to four words, in any case. The count of words, not their
 *  capitals, is the limit; the state token is checked against the real list. */
const WHOLE_PLACE_RE =
  /^([a-z][a-z'.-]*(?:\s+(?:of\s+)?[a-z][a-z'.-]*){0,3}),?\s+([a-z]{2})(?:\s+\d{5}(?:-\d{4})?)?$/i

/** One word of a city, as the place is written: a word typed all in one case is
 *  capitalised ("louisville", "LOUISVILLE" -> "Louisville", "winston-salem" ->
 *  "Winston-Salem"); a mixed-case word ("McKinney") is already written as a name. */
function cityWord(word: string): string {
  if (word !== word.toLowerCase() && word !== word.toUpperCase()) return word
  return word.toLowerCase().replace(/(^|[-'.])([a-z])/g, (_m, sep: string, ch: string) => sep + ch.toUpperCase())
}

/**
 * The city and state a deal states in its location text alone ("Louisville,
 * KY" typed into Manual Entry, no parts). The server reads the same text for
 * its locality lookup (`site_intel_store._lookup_target`), so the browser must
 * too or the two disagree about whether the deal has a place. Capitalisation
 * does not matter: text typed in as the property's address is not prose, and
 * the server raises each word before it parses. Only the whole-string form is
 * read here - the fail-closed subset of the server's parser: a street, a
 * property name or a paragraph names no city.
 */
export function statedLocality(text: string | null | undefined): { city: string; state: string } | null {
  const match = WHOLE_PLACE_RE.exec((text ?? '').replace(/\s+/g, ' ').trim())
  if (!match) return null
  const state = match[2].toUpperCase()
  if (!US_AND_CA_STATES.has(state)) return null
  // "NY, NJ" is a footprint list and "NW, WA" a quadrant, not a city.
  if (US_AND_CA_STATES.has(match[1].toUpperCase()) || QUADRANTS.has(match[1].toUpperCase())) return null
  const city = match[1]
    .split(' ')
    .map((w) => (w.toLowerCase() === 'of' ? 'of' : cityWord(w)))
    .join(' ')
  return { city, state }
}

const NONE: AssetPoint = {
  status: 'none',
  lat: null,
  lng: null,
  source: null,
  reason: 'No address on record.',
}

export function resolveAssetPoint(input: AssetPointInput): AssetPoint {
  const { resolved, master, addressBlock: block } = input
  if (!resolved && !block) return NONE

  const why = unverifiedReason(input)
  if (why) return { status: 'unverified', lat: null, lng: null, source: null, reason: why }

  const street = resolved?.street ?? null
  // A deal with no parts at all but a location text states its place there.
  const locality = !street && !resolved?.city && !resolved?.state ? statedLocality(resolved?.full) : null
  const city = resolved?.city ?? locality?.city ?? null
  const state = resolved?.state ?? locality?.state ?? null
  const geocode = masterGeocode(master)
  // A coordinate Google Places chose from a name is never a point on anything.
  // Where a street IS stated its own text is geocoded below; where none is, the
  // point is simply not used.
  const masterPoint = geocode.source === 'google-places' ? null : usable(master)

  if (street) {
    if (masterPoint && block?.masterPointApplies !== false && streetsMatch(street, master?.address)) {
      // The whole settled address, not the street alone: "100 Main St" exists in
      // every city, and without a server verdict (no block, or a server that
      // predates `masterPointApplies`) the locality and ZIP checks here are all
      // that stand between another city's master and this asset.
      if (masterPointApplies({ street, city: resolved?.city, state: resolved?.state, zip: resolved?.zip }, master)) {
        return { status: 'asset', ...masterPoint, source: 'master', reason: 'The property master\'s stored point.' }
      }
      if (geocode.precision === 'city') {
        return {
          status: 'city',
          ...masterPoint,
          source: 'master',
          reason: 'The street could only be placed at city level.',
        }
      }
    }
    let cityLevel: { lat: number; lng: number } | null = null
    // Server answers outrank the browser's: they passed the server's street-level
    // and locality gates (property_media._is_street_only, site_intel_store), and
    // the browser's geocode is the last resort for when neither has answered.
    const looked: [AssetPointSource, Point | null | undefined][] = [
      ['server', input.server],
      ['intel', input.intel],
      ['browser', input.browser],
    ]
    for (const [source, point] of looked) {
      const found = usable(point)
      if (!found) continue
      // A site lookup that geocoded only a place name has no building to offer.
      if (source === 'intel' && input.intel?.geocodePrecision === 'locality') {
        cityLevel = cityLevel ?? found
        continue
      }
      return { status: 'asset', ...found, source, reason: 'Geocoded from the stated street address.' }
    }
    if (cityLevel) {
      return {
        status: 'city',
        ...cityLevel,
        source: 'intel',
        reason: 'The street could only be placed at city level.',
      }
    }
    return {
      status: 'asset',
      lat: null,
      lng: null,
      source: null,
      reason: 'The street address has not been placed on a map yet.',
    }
  }

  if (city) {
    // The master's point belongs to the master's own place: use it only where
    // that place is the one the deal names.
    const sameState = !state || !master?.state || placesMatch(state, master.state)
    const sameCity = !master?.city || placesMatch(city, master.city)
    const masterHere = masterPoint && sameState && sameCity ? masterPoint : null
    // Where the master has no point in this city, the site lookup's own answer
    // for the city text stands in: it is a locality centre by its own label
    // (`geocodePrecision`), which is exactly what a city-level map wants. A
    // street-level or unlabelled point is a building (or an older server's
    // unknown) and is never taken for a city's centre.
    const intelCentre = input.intel?.geocodePrecision === 'locality' ? usable(input.intel) : null
    const point = masterHere ?? intelCentre
    return {
      status: 'city',
      lat: point?.lat ?? null,
      lng: point?.lng ?? null,
      source: masterHere ? 'master' : intelCentre ? 'intel' : null,
      reason: resolved?.withheld.includes('street')
        ? 'The street on record was chosen by a name lookup, not stated by a document, so the map is centered on the city and no building is shown.'
        : 'Only a city is on record, so the map is centered on it and no building is shown.',
    }
  }

  // A state alone is no place to map, but it is not "no address" either: what
  // the package stated is still on the deal and the page names it as such.
  if (isStateOnly(resolved)) {
    return { status: 'none', lat: null, lng: null, source: null, reason: STATE_ONLY_REASON }
  }

  return NONE
}
